import React, { ReactElement } from 'react';
import { useSelector } from 'react-redux';

import DirectDonorsToDonationPage from 'retired/DirectDonorsToDonationPage';
import { selectOrganization, selectUser } from 'entities/user/selectors';
import ConnectWithStripeBlock from 'containers/OrganizationStripeIntroPage/ConnectWithStripeBlock';
import RegisterWithPayPalBlock from 'containers/OrganizationPayPalIntroPage/RegisterWithPayPalBlock';
import { Features } from 'utils/features';
import { Privileges } from 'utils/privileges';
import Organization, { NonprofitEntityType } from 'entities/organization/model';

import { RegisterWithNfgBlock } from './RegisterWithNfgBlock';

const NfgSetup = () => (
  <div style={{ marginBottom: 20 }}>
    <RegisterWithNfgBlock />
  </div>
);

const PayPalSetup = ({ organization }: { organization: Organization }) => (
  <div style={{ marginBottom: 20 }}>
    <RegisterWithPayPalBlock organization={organization}>
      <li>
        <DirectDonorsToDonationPage organization={organization}>
          Once your details are confirmed by PayPal, you can direct donors to your nonprofit
          donation page by following this link:{' '}
        </DirectDonorsToDonationPage>
      </li>
    </RegisterWithPayPalBlock>
  </div>
);

const StripeSetup = ({ organization }: { organization: Organization }) => (
  <div style={{ marginBottom: 20 }}>
    <ConnectWithStripeBlock>
      <p>
        <em>
          <DirectDonorsToDonationPage organization={organization} />
        </em>
      </p>
    </ConnectWithStripeBlock>
  </div>
);

const Setups = {
  NFG: NfgSetup,
  PayPal: PayPalSetup,
  Stripe: StripeSetup,
};

export const SetupDonationProvider = (): ReactElement | null => {
  const organization = useSelector(selectOrganization);
  const user = useSelector(selectUser);

  if (!organization) {
    return null;
  }

  const isCompany = organization?.isCompany();
  const canDonate = user?.canDonate();

  const setupAllowed =
    user?.hasPrivilege(Privileges.DONATION_MANAGE_RULES) &&
    (!isCompany ||
      user?.hasFeature(Features.DONATION_MATCHING) ||
      user?.hasFeature(Features.DONATION_CREDIT));

  const getProvider = () => {
    if (!organization || !setupAllowed || canDonate || isCompany) {
      return null;
    }

    const payPalPayerId = organization?.paypalPayerId;
    const payPalIsAlreadyConfigured = !!payPalPayerId;
    const showPayPalSetup =
      !payPalIsAlreadyConfigured &&
      organization?.isInPayPalEligibleCountry() &&
      organization?.nonprofitEntityType === NonprofitEntityType.nonprofit;

    if (showPayPalSetup) {
      return 'PayPal';
    }

    const showNfgSetup =
      organization?.isInNFGEligibleCountry() &&
      (organization.ein || organization.identifiers?.some((i) => i.type === 'nces'));

    if (showNfgSetup) {
      return 'NFG';
    }

    const stripeId = organization?.stripe?.userId;
    const stripeCustomerId = organization?.stripe?.customerId;
    const stripeIsAlreadyConfigured = stripeId && stripeCustomerId;
    const showStripeSetup = !stripeIsAlreadyConfigured && organization?.isInStripeEligibleCountry();

    if (showStripeSetup) {
      return 'Stripe';
    }
    return null;
  };

  const provider = getProvider();
  const ProviderSetup = provider && Setups[provider];

  return (
    <>
      <h2>Your donations will appear below as soon as they are available</h2>
      <p>
        Deed works with a number of market-leading donation processors including: PayPal Giving
        Fund, Network for Good, GlobalGiving, Betterplace, Stripe, and others. The processor used
        depends on the location of your organization. Once a donation is made to your organization,
        you will be notified, and they will be viewable right here on your dashboard.
      </p>
      {provider && (
        <>
          <h3>
            You can now receive and raise funds for your nonprofit on the Deed app! (it’s really
            easy 🙌🏽)!
          </h3>
          <p>
            We work with an amazing list of fast-growing companies, whose employees want to donate
            to you! By signing up with our partner {provider} below, you can now raise and receive
            funds from over 200,000 potential donors on the Deed app! Just follow these simple
            steps:
          </p>
        </>
      )}
      {ProviderSetup && <ProviderSetup organization={organization} />}
    </>
  );
};
